// ============================================================================
// Sync Log Viewer
// ============================================================================
// Filterable table of sync log entries. Supports optional credential filtering.

import { useState } from 'react'
import { formatDistanceToNow, format } from 'date-fns'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useSyncLog } from '@/hooks/useIntegrations'

const RESULT_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  success: { variant: 'default', label: 'Success' },
  partial: { variant: 'secondary', label: 'Partial' },
  error: { variant: 'destructive', label: 'Error' },
  skipped: { variant: 'outline', label: 'Skipped' },
  rate_limited: { variant: 'secondary', label: 'Rate Limited' },
}

interface SyncLogViewerProps {
  credentialIds?: string[]
}

export function SyncLogViewer({ credentialIds }: SyncLogViewerProps) {
  const [resultFilter, setResultFilter] = useState<string>('all')
  const { data: entries, isLoading } = useSyncLog({
    ...(resultFilter !== 'all' ? { result: resultFilter } : {}),
    ...(credentialIds?.length ? { credentialIds } : {}),
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={resultFilter} onValueChange={setResultFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filter by result" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Results</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="rate_limited">Rate Limited</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Time</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>Result</TableHead>
              <TableHead className="text-right">Processed</TableHead>
              <TableHead className="text-right">Created</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!entries || entries.length === 0) ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No sync log entries found
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => {
                const badge = RESULT_BADGES[entry.result] ?? RESULT_BADGES.error
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs">
                      <span title={format(new Date(entry.created_at), 'PPpp')}>
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{entry.sync_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {entry.direction}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={badge.variant} className="text-xs">
                        {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs">{entry.records_processed}</TableCell>
                    <TableCell className="text-right text-xs">{entry.records_created}</TableCell>
                    <TableCell className="text-right text-xs">
                      {entry.processing_ms ? `${entry.processing_ms}ms` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-destructive max-w-[200px] truncate">
                      {entry.error_message ?? '—'}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
